import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import {
    StepComponent,
    _,
    isNotNullOrUndefined,
    ModalService,
    unitMMFormatter,
    ConfiguratorOptions,
    StartedChangingStepValue,
    StepsService,
} from '@icc/configurator/shared';
import { DoorFacade } from '../+state/door.facade';
import { WindowFacade, ProfilesModalService, BrowserProfilesService } from '@icc/configurator/window';
import {
    APP_CONFIG,
    AppConfigFactory,
    EventBusService,
    ConfigurationsService,
    WindowActiveConfiguration,
    ParametersService,
    ShapeService,
} from '@icc/common';
import {
    FormBuilder,
    FormGroup,
    Validators,
    ValidatorFn,
    AbstractControl,
    ValidationErrors,
} from '@angular/forms';
import { ResizeService } from '@icc/legacy/configurator/layout/resize.service';
import { FillingValidationService } from '@icc/legacy/configurator/steps/window/glazings/filling-validation.service';
import { SchemasService } from '@icc/legacy/configurator/steps/roller_shutter/schemas.service';
import { UnitConverterService } from '@icc/configurator/ui';
import { DimensionsService } from '@icc/legacy/configurator/steps/window/dimensions/dimensions.service';
import { DoorSizes, Shape } from '@icc/window';
import { filter, take } from 'rxjs/operators';
import { SizesService } from './sizes.service';
import { IccDoorSize, IccDoorLightsSize } from '@icc/common/data-types/DoorSize';
import { ThresholdsService } from '@icc/legacy/configurator/layout/thresholds.service';
import { ExtensionsPageComponent } from '../extensions-page/extensions-page.component';
import { PriceService } from '@icc/price';
import { LayoutService } from '@icc/legacy/configurator/layout/layout.service';
import { MeasurementsService } from '@icc/legacy/configurator/steps/window/dimensions/measurements.service';
import { ExtensionsService } from '@icc/legacy/configurator/layout/extensions.service';

@Component({
    selector: 'icc-sizes',
    templateUrl: './sizes.component.html',
    styleUrls: ['./sizes.component.scss'],
})
export class SizesComponent extends StepComponent implements OnInit, OnDestroy {
    static stepName = _('STEPS|Rozmiary');
    static stepIcon = {
        ligature: 'settings_overscan',
    };
    public configurator = 'door';
    public stepId = 'sizes';
    public title = _('DOOR|Uzupełnij rozmiary drzwi');

    private subscriptions: any[] = [];

    public doorSizes$ = this.doorFacade.doorSizes$;
    public shape$ = this.windowFacade.shape$;
    public selectedFrame$ = this.doorFacade.selectedFrame$;
    public selectedThreshold$ = this.doorFacade.selectedThreshold$;
    public seperateFrameAndSashShortening$ = this.doorFacade.seperateFrameAndSashShortening$;

    public dimensionsForm: FormGroup = this.fb.group({});
    public doorSizes: DoorSizes | null = null;
    public shape: Partial<Shape> | null = null;

    public fields: {
        type: string;
        options: Array<any>;
        name: string;
        field?: keyof DoorSizes;
        highlight?: keyof DoorSizes;
        value?: number;
        disabled?: boolean;
    }[] = [];

    private dimensionsFields: {
        type: string;
        name: string;
        field?: keyof DoorSizes;
        highlight?: keyof DoorSizes;
        options: Array<any>;
        show: (shape?: Partial<DoorSizes>) => boolean;
        value?: (shape?: Partial<DoorSizes>) => number;
        focus?: boolean;
    }[] = [
        {
            type: 'select',
            name: 'sashSize',
            field: 'sashSizeId',
            options: [],
            show: () => true,
            focus: true,
        },
        {
            type: 'select',
            name: 'passiveSashSize',
            field: 'passiveSashSizeId',
            options: [],
            show: () =>
                this.configurationsService.conf.Current.Sashes.some(sash =>
                    ['DRP', 'DOP'].includes(sash.type.type)
                ),
            focus: true,
        },
        {
            type: 'number',
            name: 'customWidth',
            field: 'customWidth',
            options: [],
            show: () => this.doorSizes.customSize,
        },
        {
            type: 'number',
            name: 'customHeight',
            field: 'customHeight',
            options: [],
            show: () => this.doorSizes.customSize,
        },
        {
            type: 'select',
            options: [],
            name: 'shortening',
            field: 'shortening',
            show: () => !this.doorSizes.customSize,
        },
        {
            type: 'select',
            options: [],
            name: 'frameShortening',
            field: 'frameShortening',
            show: () => this.configurationsService.conf.Current.System.seperate_frame_and_sash_shortening && !this.doorSizes.customSize
        },
        {
            type: 'number',
            name: 'leftLightSize',
            field: 'leftLightSize',
            options: [],
            show: () =>
                this.sideLightsSizes.length === 1
                && this.sideLightsSizes[0].size_from !== this.sideLightsSizes[0].size_to
                && this.doorSizes.leftLightSize > 0,
        },
        {
            type: 'select',
            name: 'leftLightSize',
            field: 'leftLightSizeId',
            options: [],
            show: () =>
                (this.sideLightsSizes.length > 1
                    || (this.sideLightsSizes.length === 1
                        && this.sideLightsSizes[0].size_from === this.sideLightsSizes[0].size_to))
                && this.doorSizes.leftLightSize > 0,
        },
        {
            type: 'number',
            name: 'rightLightSize',
            field: 'rightLightSize',
            options: [],
            show: () =>
                this.sideLightsSizes.length === 1
                && this.sideLightsSizes[0].size_from !== this.sideLightsSizes[0].size_to
                && this.doorSizes.rightLightSize > 0,
        },
        {
            type: 'select',
            name: 'rightLightSize',
            field: 'rightLightSizeId',
            options: [],
            show: () =>
                (this.sideLightsSizes.length > 1
                    || (this.sideLightsSizes.length === 1
                        && this.sideLightsSizes[0].size_from === this.sideLightsSizes[0].size_to))
                && this.doorSizes.rightLightSize > 0,
        },
        {
            type: 'number',
            name: 'topLightSize',
            field: 'topLightSize',
            options: [],
            show: () =>
                this.topLightsSizes.length === 1
                && this.topLightsSizes[0].size_from !== this.topLightsSizes[0].size_to
                && this.doorSizes.topLightSize > 0,
        },
        {
            type: 'select',
            name: 'topLightSize',
            field: 'topLightSizeId',
            options: [],
            show: () =>
                (this.topLightsSizes.length > 1
                    || (this.topLightsSizes.length === 1
                        && this.topLightsSizes[0].size_from === this.topLightsSizes[0].size_to))
                && this.doorSizes.topLightSize > 0,
        },
    ];

    dimensionUnit = this.unitConverterService.getUnit();
    sashSizes: IccDoorSize[] = [];
    passiveSashSizes: IccDoorSize[] = [];
    sideLightsSizes: IccDoorLightsSize[] = [];
    topLightsSizes: IccDoorLightsSize[] = [];

    static stepEnable = (conf: WindowActiveConfiguration, options: ConfiguratorOptions) =>
        conf.System && Boolean(conf.System.door_type);

    constructor(
        private doorFacade: DoorFacade,
        private windowFacade: WindowFacade,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private fb: FormBuilder,
        private dimensionsService: DimensionsService,
        private stepsService: StepsService,
        private resizeService: ResizeService,
        private fillingValidationService: FillingValidationService,
        private configurationsService: ConfigurationsService<'door'>,
        private priceService: PriceService,
        private parametersService: ParametersService,
        private layoutService: LayoutService,
        private schemasService: SchemasService,
        private unitConverterService: UnitConverterService,
        private measurementsService: MeasurementsService,
        private extensionsService: ExtensionsService,
        private shapeService: ShapeService,
        private sizesService: SizesService,
        private profilesModalService: ProfilesModalService,
        private thresholdsService: ThresholdsService,
        private modalService: ModalService,
        private profilesService: BrowserProfilesService,
        private eventBusService: EventBusService
    ) {
        super();
        this.eventBusService.subscribeWithoutConfiguration('initializedConfigurator', () => {
            this.init();
        });

        this.subscriptions.push(
            this.eventBusService.subscribe<StartedChangingStepValue>('startedChangingStep', data => {
                if (
                    data.value.nextStep.code !== 'sizes'
                    && data.value.nextStep.code !== data.value.prevStep.code
                    && data.value.prevStep.code === 'sizes'
                ) {
                    if (!this.dimensionsForm.valid || !this.dimensionsService.valid(this.shape)) {
                        data.value.cancel();
                    }
                }
            })
        );
    }

    ngOnInit() {}

    ngOnDestroy() {
        this.subscriptions.map(el => el.unsubscribe());
    }

    private init() {
        const conf = this.configurationsService.conf.Current;
        this.sashSizes = this.sizesService.getSashSizes(conf);
        this.passiveSashSizes = this.sizesService.getPassiveSashSizes(conf);
        this.sideLightsSizes = this.sizesService.getSideLightsSizes();
        this.topLightsSizes = this.sizesService.getTopLightsSizes();
        this.subscriptions.push(
            this.shape$.pipe(filter(isNotNullOrUndefined)).subscribe(shape => {
                this.shape = shape;
            })
        );
        this.subscriptions.push(
            this.doorSizes$.pipe(filter(isNotNullOrUndefined), take(1)).subscribe(doorSizes => {
                if (!this.fields || this.fields.length === 0) {
                    this.generateFields(doorSizes);
                }
            })
        );
    }

    private generateFields(doorSizes: DoorSizes) {
        this.doorSizes = doorSizes;
        const group = this.fb.group({});
        const fields = this.dimensionsFields
            .filter(f => f.show())
            .map(f => {
                let options = f.options;
                let validators = [];
                let hintParams = null;
                let step = 1;
                let disabled = false;
                if (f.field === 'shortening') {
                    options = this.buildShorteningOptions();
                } else if (f.field === 'frameShortening') {
                    options = this.buildFrameShorteningOptions(doorSizes.shortening);
                    disabled = options.length < 2;
                } else if (f.field === 'sashSizeId') {
                    options = this.sashSizes.map(v => ({
                        name: v.name,
                        value: v.id,
                    }));
                } else if (f.field === 'passiveSashSizeId') {
                    options = this.passiveSashSizes.map(v => ({
                        name: v.name,
                        value: v.id,
                    }));
                } else if (f.field === 'leftLightSizeId') {
                    options = this.sideLightsSizes.map(v => ({
                        name: v.name,
                        value: v.id,
                    }));
                } else if (f.field === 'leftLightSize') {
                    validators = [
                        Validators.min(this.sideLightsSizes[0].size_from),
                        Validators.max(this.sideLightsSizes[0].size_to),
                        SizesComponent.step(this.sideLightsSizes[0].size_interval || 1),
                    ];
                    hintParams = {
                        min: this.formatUnit(this.sideLightsSizes[0].size_from),
                        max: this.formatUnit(this.sideLightsSizes[0].size_to),
                        step: this.formatUnit(this.sideLightsSizes[0].size_interval),
                    };
                    step = this.sideLightsSizes[0].size_interval;
                } else if (f.field === 'rightLightSizeId') {
                    options = this.sideLightsSizes.map(v => ({
                        name: v.name,
                        value: v.id,
                    }));
                } else if (f.field === 'rightLightSize') {
                    validators = [
                        Validators.min(this.sideLightsSizes[0].size_from),
                        Validators.max(this.sideLightsSizes[0].size_to),
                        SizesComponent.step(this.sideLightsSizes[0].size_interval || 1),
                    ];
                    hintParams = {
                        min: this.formatUnit(this.sideLightsSizes[0].size_from),
                        max: this.formatUnit(this.sideLightsSizes[0].size_to),
                        step: this.formatUnit(this.sideLightsSizes[0].size_interval),
                    };
                    step = this.sideLightsSizes[0].size_interval;
                } else if (f.field === 'topLightSizeId') {
                    options = this.topLightsSizes.map(v => ({
                        name: v.name,
                        value: v.id,
                    }));
                } else if (f.field === 'topLightSize') {
                    validators = [
                        Validators.min(this.topLightsSizes[0].size_from),
                        Validators.max(this.topLightsSizes[0].size_to),
                        SizesComponent.step(this.topLightsSizes[0].size_interval || 1),
                    ];
                    hintParams = {
                        min: this.formatUnit(this.topLightsSizes[0].size_from),
                        max: this.formatUnit(this.topLightsSizes[0].size_to),
                        step: this.formatUnit(this.topLightsSizes[0].size_interval),
                    };
                    step = this.topLightsSizes[0].size_interval;
                } else if (f.field === 'customWidth') {
                    const range = this.sizesService.getRange(doorSizes);
                    validators = [];
                    hintParams = {
                        min: this.formatUnit(range.minX),
                        max: this.formatUnit(range.maxX),
                        step: this.formatUnit(range.step),
                    };
                    step = range.step;
                } else if (f.field === 'customHeight') {
                    const range = this.sizesService.getRange(doorSizes);
                    validators = [];
                    hintParams = {
                        min: this.formatUnit(range.minY),
                        max: this.formatUnit(range.maxY),
                        step: this.formatUnit(range.step),
                    };
                    step = range.step;
                }
                return {
                    type: f.type,
                    options,
                    disabled,
                    name: f.name,
                    field: f.field,
                    step,
                    validators,
                    hintParams,
                    highlight: f.highlight || f.field,
                };
            });

        fields.forEach(field => {
            if (field.field) {
                group.addControl(
                    field.field,
                    this.fb.control(doorSizes[field.field], {
                        updateOn: 'blur',
                        validators: field.validators
                    })
                );
            }
        });
        if (this.doorSizes.customSize) {
            group.setValidators(this.sizeRangeMatchValidator().bind(this));
        }
        this.dimensionsForm = group;

        this.subscriptions.push(
            this.dimensionsForm.valueChanges.subscribe((value: DoorSizes) => {
                const newDoorSizes: DoorSizes = {
                    ...doorSizes,
                    ...value,
                };
                this.setTempShapeDimensions(
                    newDoorSizes,
                    value.leftLightSizeId
                    && value.leftLightSizeId !== doorSizes.leftLightSizeId,
                    value.rightLightSizeId
                    && value.rightLightSizeId !== doorSizes.rightLightSizeId,
                    value.topLightSizeId && value.topLightSizeId !== doorSizes.topLightSizeId,
                    value.sashSizeId && value.sashSizeId !== doorSizes.sashSizeId,
                );
                if (this.dimensionsForm.valid && this.dimensionsService.valid(this.shape)) {
                    this.resizeLayout();
                    this.fillingValidationService.valid();
                    this.eventBusService.post({
                        key: 'icc-redraw',
                        value: {},
                    });
                }
            }),
        );

        const frameShorteningControl = this.dimensionsForm.get('frameShortening');
        if (frameShorteningControl) {
            this.subscriptions.push(
                this.dimensionsForm.get('shortening').valueChanges.subscribe(v => {
                    this.rebuildFrameShorteningOptions(v);
                })
            )
        }

        this.fields = fields;
    }

    resizeLayout() {
        const conf = this.configurationsService.conf.Current;
        const oldWidth = conf.Width;
        const oldHeight = conf.Height;

        this.sizesService.setSizes(this.doorSizes, conf);
        this.dimensionsService.setDimensions(this.shape);
        this.resizeService.resizeLayout(conf.Width - oldWidth, conf.Height - oldHeight, conf);
        this.dimensionsService.validDoorModelDimensions(this.shape);
        if (!this.config().IccConfig.Configurators.roller_shutter.extraDimensionsOptions) {
            this.schemasService.setShuttersBySchema({
                width: this.shape.width,
                height: this.shape.height,
                nextStep: false,
            });
        }

        conf.ChangedDimensions = false;
    }

    buildShorteningOptions() {
        const shorteningRange = this.sizesService.getSashesShorteningRange();
        return shorteningRange.map(i => ({ name: i,value: i }));
    }

    buildFrameShorteningOptions(shortening?: number) {
        const options = [];
        if (shortening > 0) {
            options.push(shortening);
        }
        options.push(0)
        return options
            .map(i => ({
                name: i,
                value: i,
            }));
    }

    rebuildFrameShorteningOptions(shortening?: number) {
        const options = this.buildFrameShorteningOptions(shortening);
        const frameShorteningField = this.fields.find(f => f.name === 'frameShortening');
        if (frameShorteningField) {
            frameShorteningField.options = options;
            frameShorteningField.disabled = options.length < 2; // Disable field
            this.dimensionsForm.get('frameShortening').setValue(options[0].value);
        }
    }

    setTempShapeDimensions(
        doorSizes: DoorSizes,
        changedLeftLightSizeId: boolean,
        changedRightLightSizeId: boolean,
        changedTopLightSizeId: boolean,
        changedSashSizeId: boolean,
    ) {
        if (changedLeftLightSizeId && doorSizes.leftLightSizeId) {
            const size = this.sideLightsSizes.find(s => s.id === doorSizes.leftLightSizeId);
            if (doorSizes.leftLightSize) {
                doorSizes.leftLightSize = size.size_from;
            }
        }
        if (changedRightLightSizeId && doorSizes.rightLightSizeId) {
            const size = this.sideLightsSizes.find(s => s.id === doorSizes.rightLightSizeId);
            if (doorSizes.rightLightSize) {
                doorSizes.rightLightSize = size.size_from;
            }
        }
        if (changedTopLightSizeId && doorSizes.topLightSizeId) {
            const size = this.topLightsSizes.find(s => s.id === doorSizes.topLightSizeId);
            if (doorSizes.topLightSize) {
                doorSizes.topLightSize = size.size_from;
            }
        }
        if (changedSashSizeId && doorSizes.sashSizeId) {
            const size = this.sashSizes.find(s => s.id === doorSizes.sashSizeId);
            doorSizes.customSize = size.custom;
            doorSizes.customWidth = size.default_width || 0;
            doorSizes.customHeight = size.default_height || 0;
            this.generateFields(doorSizes);
        }
        this.doorSizes = doorSizes;
        this.shape = this.sizesService.getShapeFromDoorSizes(
            doorSizes,
            this.configurationsService.conf.Current
        );
        this.dimensionsService.changedDimensions();
    }

    changeFrame() {
        const conf = this.configurationsService.conf.Current;
        const defaultConf = this.configurationsService.conf.Default;
        const frame = conf.Frames.find(f => f.lowThreshold) || conf.Frames[0];
        this.profilesModalService.changeProfile(conf, defaultConf, 'frame', {
            position: 'allFrames',
            frame,
        });
    }

    changeThreshold() {
        const conf = this.configurationsService.conf.Current;
        const threshold = this.profilesService.getProfile(this.profilesService.getUsedThresholdId(conf));
        this.profilesModalService
            .openProfilesModal(
                conf,
                'threshold',
                {
                    and: [],
                    not: [],
                },
                threshold
            )
            .then(profile => {
                if (profile) {
                    this.thresholdsService.changeLowThreshold(
                        profile.id,
                        this.configurationsService.conf.Current
                    );
                }
            });
    }

    changeExtensions() {
        this.modalService.open({
            pageComponent: ExtensionsPageComponent,
        });
    }

    anyAvailableExtensions(): boolean {
        const profiles = this.profilesService.getFilteredProfiles(this.configurationsService.conf.Current, ['extension', 'sandwich'], [{}], false);
        return profiles && Array.isArray(profiles) && profiles.length > 0;
    }

    private formatUnit(value: number) {
        return unitMMFormatter(value, this.config().IccConfig, 1, 0, this.dimensionUnit.unit);
    }

    static step(step: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value == null || step == null) {
                return null; // don't validate empty values to allow optional controls
            }
            const value = parseFloat(control.value);
            // Controls with NaN values after parsing should be treated as not having a
            // minimum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
            return !isNaN(value) && value % step !== 0
                ? { step: { step: step, actual: control.value } }
                : null;
        };
    }

    sizeRangeMatchValidator(): ValidatorFn {
        return (form: FormGroup): ValidationErrors | null => {
            const heightControl = form.get('customHeight');
            const widthControl = form.get('customWidth');
            if (heightControl?.value == null || widthControl?.value == null) {
                return null; // don't validate empty values to allow optional controls
            }
            const height = parseFloat(heightControl?.value);
            const width = parseFloat(widthControl?.value);

            if (isNaN(height) || isNaN(width)) {
                heightControl?.setErrors(null);
                widthControl?.setErrors(null);
                return null;
            }

            const range = this.sizesService.getRange(this.doorSizes);
            const widthRange = this.sizesService.getWidthRange(this.doorSizes, height);
            const heightRange = this.sizesService.getHeightRange(this.doorSizes, width);
            const heightInRange = height <= range.maxY && height >= range.minY;
            const widthInRange = width <= range.maxX && width >= range.minX;

            if (heightInRange && !widthInRange) {
                heightControl?.setErrors(null);
                widthControl?.setErrors({ 
                    range: {
                        min: this.formatUnit(range.minX),
                        max: this.formatUnit(range.maxX),
                        step: this.formatUnit(range.step)
                    }
                });
                return { range: true };
            }

            if (!heightInRange && widthInRange) {
                widthControl?.setErrors(null);
                heightControl?.setErrors({
                    range: {
                        min: this.formatUnit(range.minY),
                        max: this.formatUnit(range.maxY),
                        step: this.formatUnit(range.step)
                    }
                });
                return { range: true };
            }

            if (!heightInRange && !widthInRange) {
                heightControl?.setErrors({ range: {
                    min: this.formatUnit(range.minY),
                    max: this.formatUnit(range.maxY),
                    step: this.formatUnit(range.step)
                }});
                widthControl?.setErrors({ range: {
                    min: this.formatUnit(range.minX),
                    max: this.formatUnit(range.maxX),
                    step: this.formatUnit(range.step)
                }});
                return { range: true };
            }

            if (!this.sizesService.isMatchingDimensions(this.doorSizes, width, height)) {
                if (width !== this.doorSizes.customWidth) {
                    widthControl?.setErrors({ range: {
                        min: this.formatUnit(widthRange.min),
                        max: this.formatUnit(widthRange.max),
                        step: this.formatUnit(widthRange.step)
                    }});
                    return { range: true };
                }
                if (height !== this.doorSizes.customHeight) {
                    heightControl?.setErrors({ range: {
                        min: this.formatUnit(heightRange.min),
                        max: this.formatUnit(heightRange.max),
                        step: this.formatUnit(heightRange.step)
                    }});
                    return { range: true };
                }
            }

            heightControl?.setErrors(null);
            widthControl?.setErrors(null);
            return null;
        };
    }

    
    round(value: number, step: number) {
        return Math.ceil(value / step) * step;
    }
}
